<template>
  <div>
    <br>
    <v-card class="mx-auto" width="90%" style="margin-bottom: 15px;">
      <v-container fluid>
        <v-row>
          <v-col cols="12">
            <v-card flat>
              <v-card-title class="headline mb-1">
                <v-icon class="mr-2">mdi-download</v-icon>
                {{ type === 'plan' ? $t('views.ExportData.downloadPlanJson') : $t('views.ExportData.downloadModelJson') }}
              </v-card-title>
              <v-card-text>
                <!-- A way from the alert in Slack to the model itself. Only for a model that is
                     still there: one in the trash has nothing to open until it is restored. -->
                <div v-if="canOpenModel" class="mb-4">
                  <div v-if="modelData.name" class="text-subtitle-1 mb-2">{{ modelData.name }}</div>
                  <v-btn outlined color="primary" :loading="openingModel" @click="openModelOverview">
                    <v-icon left>mdi-chart-box-outline</v-icon>
                    {{ $t('views.ExportData.openModelOverview') }}
                  </v-btn>
                  <div v-if="openModelError" class="error--text text-caption mt-2">{{ $t('views.ExportData.openModelError') }}</div>
                </div>
                <!-- Shown once a download has fallen back to the copy the queue kept: it sits
                     outside the loading/error/success branches so it stays visible next to the
                     success message the user ends up on. -->
                <v-alert v-if="fromQueueCopy" type="warning" text dense class="text-left mb-4">
                  <div class="font-weight-medium">{{ $t('views.ExportData.deletedPlanTitle') }}</div>
                  <div class="text-body-2">{{ $t('views.ExportData.deletedPlanExplanation') }}</div>
                  <div v-if="queuedOn" class="text-caption mt-1">{{ $t('views.ExportData.deletedPlanQueuedOn', { date: queuedOn }) }}</div>
                </v-alert>
                <!-- Same idea for a model read from the trash, known as soon as the page loads. -->
                <v-alert v-if="trash" type="warning" text dense class="text-left mb-4">
                  <div class="font-weight-medium">{{ $t('views.ExportData.deletedModelTitle') }}</div>
                  <div v-if="type === 'model'" class="text-body-2">{{ $t('views.ExportData.deletedModelExplanation') }}</div>
                  <div v-if="trashDeletedOn" class="text-caption mt-1">{{ trashDeletedText }}</div>
                  <div v-if="trashPurgeOn" class="text-caption">{{ $t('views.ExportData.deletedModelPurgeOn', { date: trashPurgeOn }) }}</div>
                </v-alert>
                <div v-if="loading">
                  <v-progress-circular indeterminate color="primary" class="mb-3"></v-progress-circular>
                  <div>{{ type === 'plan' ? $t('views.ExportData.preparingPlanDownload') : $t('views.ExportData.preparingModelDownload') }}</div>
                </div>
                <div v-else-if="error">
                  <v-icon color="error" size="48" class="mb-3">mdi-alert-circle</v-icon>
                  <div class="error--text">{{ error }}</div>
                  <v-btn color="primary" @click="exportData" class="mt-3">
                    <v-icon left>mdi-refresh</v-icon>
                    {{ $t('views.ExportData.tryAgain') }}
                  </v-btn>
                </div>
                <div v-else-if="success">
                  <v-icon color="success" size="48" class="mb-3">mdi-check-circle</v-icon>
                  <div class="success--text">{{ type === 'plan' ? $t('views.ExportData.planDownloadSuccess') : $t('views.ExportData.modelDownloadSuccess') }}</div>
                  <div class="text-caption mt-2">{{ $t('views.ExportData.closeTabMessage') }}</div>
                </div>
                <div v-else>
                  <div class="mb-3">{{ type === 'plan' ? $t('views.ExportData.readyToDownloadPlan') : $t('views.ExportData.readyToDownloadModel') }}</div>
                  <v-btn color="primary" large @click="exportData">
                    <v-icon left>mdi-download</v-icon>
                    {{ type === 'plan' ? $t('views.ExportData.downloadPlanJson') : $t('views.ExportData.downloadModelJson') }}
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-card>

    <!-- PKL Download Section - For Ad-Hoc, Custom, EKRM and Automatic models -->
    <v-card v-if="type === 'model' && supportsPklDownload" class="mx-auto" width="90%" style="margin-bottom: 15px;">
      <v-container fluid>
        <v-row>
          <v-col cols="12">
            <v-card flat>
              <v-card-title class="headline mb-1">
                <v-icon class="mr-2">mdi-file-download</v-icon>
                {{ $t('views.ExportData.downloadModelPklFiles') }}
              </v-card-title>
              <v-card-text>
                <div v-if="loadingPkl">
                  <v-progress-circular indeterminate color="primary" class="mb-3"></v-progress-circular>
                  <div>{{ $t('views.ExportData.loadingPklInfo') }}</div>
                </div>
                <div v-else>
                  <div v-if="hasModelPkl || hasPlanPkl">
                    <p class="text-body-1 mb-3">{{ $t('views.ExportData.pklFilesDescription') }}</p>

                    <!-- Main Model PKL -->
                    <div v-if="hasModelPkl" class="mb-3">
                      <v-btn
                        v-if="modelPklUrl"
                        color="primary"
                        @click="downloadPklFile(modelPklUrl, modelPklFileName)"
                        large
                      >
                        <v-icon left>mdi-download</v-icon>
                        {{ $t('views.ExportData.downloadModelPkl') }}
                      </v-btn>
                      <div v-else class="text-caption text--secondary">
                        <div>{{ $t('views.ExportData.modelPklNotAvailable') }}</div>
                        <div v-if="modelPklError">{{ modelPklError }}</div>
                      </div>
                    </div>

                    <!-- Plan Model PKL -->
                    <div v-if="hasPlanPkl" class="mb-3">
                      <v-btn
                        v-if="planPklUrl"
                        color="secondary"
                        large
                        @click="downloadPklFile(planPklUrl, planPklFileName)"
                      >
                        <v-icon left>mdi-download</v-icon>
                        {{ $t('views.ExportData.downloadPlanPkl') }}
                      </v-btn>
                      <div v-else class="text-caption text--secondary">
                        <div>{{ $t('views.ExportData.planPklNotAvailable') }}</div>
                        <div v-if="planPklError">{{ planPklError }}</div>
                      </div>
                    </div>
                  </div>
                  <div v-else>
                    <v-icon color="info" size="48" class="mb-3">mdi-information</v-icon>
                    <div class="text-body-1">{{ $t('views.ExportData.noPklFilesAvailable') }}</div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-card>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'ExportData',
  props: {
    type: {
      type: String,
      required: true,
      validator: value => ['plan', 'model'].includes(value)
    },
    clientId: {
      type: String,
      required: true
    },
    simulationId: {
      type: String,
      required: false
    }
  },
  data () {
    return {
      loading: false,
      error: null,
      success: false,
      modelData: null,
      loadingModelData: false,
      modelPklUrl: null,
      planPklUrl: null,
      modelPklError: null,
      planPklError: null,
      loadingPkl: false,
      fromQueueCopy: false,
      queueMetadata: null,
      trash: null,
      openingModel: false,
      openModelError: false
    }
  },
  computed: {
    ...mapGetters('auth', ['authUser']),
    ...mapGetters('clients', ['clients']),
    canOpenModel () {
      return !!this.modelData && !this.trash
    },
    // Date the queue copy was taken, shown in the deleted-plan notice when the queue item has one.
    queuedOn () {
      return this.formatDate(this.queueMetadata ? this.queueMetadata.date_queued : null)
    },
    trashDeletedOn () {
      return this.formatDate(this.trash ? this.trash.date_deleted : null)
    },
    trashPurgeOn () {
      return this.formatDate(this.trash ? this.trash.purge_after : null)
    },
    trashDeletedText () {
      var key = this.trash.deleted_by_name ? 'deletedModelDeletedOnBy' : 'deletedModelDeletedOn'
      return this.$t('views.ExportData.' + key, { date: this.trashDeletedOn, name: this.trash.deleted_by_name })
    },
    modelType () {
      return this.modelData && this.modelData.type ? this.modelData.type : null
    },
    isAdHocModel () {
      return this.modelType === 'mmm'
    },
    isCustomModel () {
      return this.modelType === 'custom'
    },
    isEkrmModel () {
      return this.modelType === 'ekrm'
    },
    isAutomaticModel () {
      return this.modelType === 'automatic'
    },
    supportsPklDownload () {
      return this.isAdHocModel || this.isCustomModel || this.isEkrmModel || this.isAutomaticModel
    },
    referenceModel () {
      return this.modelData && this.modelData.reference_model ? this.modelData.reference_model : null
    },
    modelCodename () {
      return this.referenceModel ? this.referenceModel.codename : null
    },
    // EKRM models have no codename: their reference_model is a verbatim copy of the
    // ekrm/{id} document (see ekrmBuilt in functions/index.js and the ekrmModelsObj
    // map in the manager store), and that document never carries its own id. The only
    // trace of it on the client doc is pkl.name, written as `${ekrmId}.pkl`.
    ekrmModelId () {
      var pkl = this.referenceModel ? this.referenceModel.pkl : null
      var name = pkl && typeof pkl.name === 'string' ? pkl.name : ''
      return /^[^/]+\.pkl$/i.test(name) ? name.slice(0, -4) : null
    },
    modelPklPath () {
      if (this.isAdHocModel) {
        return this.modelCodename ? `models/mmm/${this.modelCodename}.pkl` : null
      }
      if (this.isEkrmModel) {
        return this.ekrmModelId ? `models/ekrm/${this.ekrmModelId}.pkl` : null
      }
      // Custom models point at the shared reference-model PKL; automatic models get
      // their own copy written to models/{clientId}.pkl, with codename === clientId.
      if (this.isCustomModel || this.isAutomaticModel) {
        return this.modelCodename ? `models/${this.modelCodename}.pkl` : null
      }
      return null
    },
    planPklPath () {
      // Only Ad-Hoc models have plan PKL files
      return this.isAdHocModel && this.modelCodename ? `models/mmm/${this.modelCodename}_plan.pkl` : null
    },
    hasModelPkl () {
      return !!(this.supportsPklDownload && this.referenceModel && this.referenceModel.pkl)
    },
    hasPlanPkl () {
      // Only Ad-Hoc models have plan PKL files
      return !!(this.isAdHocModel && this.modelData.plan_reference_model && this.modelData.plan_reference_model.pkl)
    },
    modelPklFileName () {
      if (this.modelCodename) {
        return `${this.modelCodename}.pkl`
      }
      // EKRM models are named after their category, mirroring the download offered
      // by Manage EKRM, since the raw document id means nothing to the user.
      var name = this.modelData && (this.modelData.code || this.modelData.name)
      return name ? `${name}.pkl` : 'model.pkl'
    },
    planPklFileName () {
      return this.modelCodename ? `${this.modelCodename}_plan.pkl` : 'model_plan.pkl'
    }
  },
  async mounted () {
    // Load model data to know whether the model is still there and, on the model page, whether
    // it has PKL files
    await this.loadModelData()
    await this.loadPklUrls()
  },
  methods: {
    ...mapActions('clients', ['exportModelData', 'loadNewClient', 'setCurrentClient']),
    ...mapActions('simulations', ['exportSimulationData']),
    ...mapActions('storage', ['getFileDownloadUrl']),
    async exportData () {
      // Check if user is authenticated before starting export
      if (!this.authUser || !this.authUser.uid) {
        this.error = this.$t('views.ExportData.authenticationRequired')
        return
      }

      this.loading = true
      this.error = null
      this.success = false

      try {
        let data = null
        let fileName = ''

        if (this.type === 'plan') {
          // Fetch simulation data using store action
          const result = await this.exportSimulationData({
            clientId: this.clientId,
            simulationId: this.simulationId
          })
          data = result.data
          this.queueMetadata = result.queueMetadata
          this.fromQueueCopy = result.source === 'queue'
          const namePrefix = this.fromQueueCopy ? 'plan_queued' : 'plan'
          if (this.fromQueueCopy) {
            // Mark the file itself, so a JSON that leaves the browser still says where it came
            // from and that it holds the plan's inputs rather than a completed run.
            data = Object.assign({ _source: 'queue_snapshot', _queue_metadata: this.queueMetadata }, data)
          }
          fileName = `${namePrefix}_${this.clientId}_${this.simulationId}_${new Date().toISOString().split('T')[0]}.json`
        } else {
          // Fetch client/model data using store action
          const result = await this.exportModelData({
            clientId: this.clientId
          })
          data = result.data
          this.trash = result.source === 'trash' ? result.trash : null
          const namePrefix = this.trash ? 'model_deleted' : 'model'
          if (this.trash) {
            // Mark the file itself, as with the queue copy of a plan, so it still says the model
            // had been deleted once it leaves the browser.
            data = Object.assign({ _source: 'trash_snapshot', _trash_metadata: this.trash }, data)
          }
          fileName = `${namePrefix}_${this.clientId}_${new Date().toISOString().split('T')[0]}.json`
        }

        if (!data) {
          throw new Error(this.$t('views.ExportData.noDataFound'))
        }

        // Create and download the JSON file
        this.downloadJson(data, fileName)
        this.success = true
      } catch (error) {
        console.error('Export error:', error)
        this.error = this.describeExportError(error)
      } finally {
        this.loading = false
      }
    },

    // The Cloud Function behind both exports answers with a code rather than a text to show.
    describeExportError (error) {
      var code = error && error.code ? String(error.code).replace('functions/', '') : ''
      if (code === 'not-found') {
        return this.$t(this.type === 'plan' ? 'views.ExportData.planNotFound' : 'views.ExportData.modelNotFound')
      }
      if (code === 'permission-denied') {
        return this.$t('views.ExportData.permissionDenied')
      }
      return (error && error.message) || this.$t('views.ExportData.exportError')
    },

    // Dates reach this page as a string, as milliseconds or as the { seconds, nanoseconds } a
    // Firestore timestamp turns into on its way from the Cloud Function.
    formatDate (raw) {
      if (!raw) return null
      var date = typeof raw === 'object' && typeof raw.seconds === 'number' ? new Date(raw.seconds * 1000) : new Date(raw)
      return isNaN(date.getTime()) ? null : date.toLocaleString()
    },

    downloadJson (data, fileName) {
      // Create a blob with the JSON data
      const jsonString = JSON.stringify(data, null, 2)
      const blob = new Blob([jsonString], { type: 'application/json' })

      // Create a temporary URL for the blob
      const url = URL.createObjectURL(blob)

      // Create a temporary anchor element and trigger the download
      const link = document.createElement('a')
      link.href = url
      link.download = fileName
      document.body.appendChild(link)
      link.click()

      // Clean up
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    },

    async loadModelData () {
      if (!this.clientId) return

      this.loadingModelData = true
      try {
        // Use the same action that's used in exportData
        const result = await this.exportModelData({
          clientId: this.clientId
        })
        this.modelData = result.data
        this.trash = result.source === 'trash' ? result.trash : null
      } catch (error) {
        console.error('Failed to load model data:', error)
      } finally {
        this.loadingModelData = false
      }
    },

    async loadPklUrls () {
      if (this.type !== 'model' || !this.supportsPklDownload) return

      this.loadingPkl = true
      this.modelPklError = null
      this.planPklError = null
      try {
        // Load main PKL URL if it exists
        if (this.hasModelPkl) {
          if (!this.modelPklPath) {
            this.modelPklError = this.$t('views.ExportData.pklReasonNoPath')
            console.warn('Could not resolve the storage path for the model PKL:', this.modelType, this.referenceModel.pkl)
          } else {
            try {
              this.modelPklUrl = await this.getFileDownloadUrl({
                path: this.modelPklPath
              })
            } catch (error) {
              this.modelPklError = this.describePklError(error, this.modelPklPath)
              console.warn('Model PKL file not found or inaccessible:', this.modelPklPath, error)
            }
          }
        }

        // Load plan PKL URL if it exists (only for Ad-Hoc models)
        if (this.hasPlanPkl && this.planPklPath) {
          try {
            this.planPklUrl = await this.getFileDownloadUrl({
              path: this.planPklPath
            })
          } catch (error) {
            this.planPklError = this.describePklError(error, this.planPklPath)
            console.warn('Plan PKL file not found or inaccessible:', this.planPklPath, error)
          }
        }
      } catch (error) {
        console.error('Failed to load PKL URLs:', error)
      } finally {
        this.loadingPkl = false
      }
    },

    // Storage failures used to be swallowed into a bare "not available", which cannot
    // be told apart from a missing file. Name the cause instead: reading models/** is
    // limited to Scientist and Admin by storage.rules, the same two roles this route is for.
    describePklError (error, path) {
      var code = error && error.code ? error.code : ''
      if (code === 'storage/unauthorized' || code === 'storage/unauthenticated') {
        return this.$t('views.ExportData.pklReasonUnauthorized')
      }
      if (code === 'storage/object-not-found') {
        return this.$t('views.ExportData.pklReasonNotFound', { path: path })
      }
      return this.$t('views.ExportData.pklReasonUnknown', {
        error: code || (error && error.message ? error.message : String(error))
      })
    },

    // Selects the model, as picking it in the model selector would, and opens its Model Overview.
    // Support rarely has the model among its own, so it is loaded first, as Model Composer does.
    async openModelOverview () {
      this.openingModel = true
      this.openModelError = false
      try {
        if (!this.clients.some(client => client.id === this.clientId)) {
          await this.loadNewClient({ id: this.clientId })
        }
        this.setCurrentClient(this.clientId)
        this.$router.push({ name: 'modelReport' })
      } catch (error) {
        console.error('Could not open the model:', error)
        this.openModelError = true
      } finally {
        this.openingModel = false
      }
    },

    downloadPklFile (url, fileName) {
      if (!url) return

      const link = document.createElement('a')
      link.href = url
      link.download = fileName
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }
}
</script>

<style scoped>
</style>
